declare global {
  interface Window { jspdf: any; }
}

let jsPdfLoading: Promise<void> | null = null;
function ensureJsPDF(): Promise<void> {
  if (window.jspdf) return Promise.resolve();
  if (jsPdfLoading) return jsPdfLoading;
  jsPdfLoading = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Falha ao carregar a biblioteca de PDF'));
    document.head.appendChild(s);
  });
  return jsPdfLoading;
}

interface EmpLike {
  nome: string;
  cpf?: string;
  funcao?: string;
  camarote?: string;
}

export async function generateReceiptPDF(titulo: string, emp: EmpLike, campos: Record<string, string>, fotoDataUrl: string | null, hash: string | null) {
  try {
    await ensureJsPDF();
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF();
    let y = 18;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14);
    doc.text(titulo, 14, y); y += 9;
    doc.setDrawColor(200); doc.line(14, y - 4, 196, y - 4);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    doc.text(`Funcionário: ${emp.nome}`, 14, y); y += 6;
    doc.text(`CPF: ${emp.cpf || '—'}    Função: ${emp.funcao || '—'}    Camarote: ${emp.camarote || '—'}`, 14, y); y += 8;
    Object.entries(campos).forEach(([k, v]) => { doc.text(`${k}: ${v}`, 14, y); y += 6; });
    y += 2;
    if (fotoDataUrl) {
      try { doc.addImage(fotoDataUrl, 'JPEG', 14, y, 55, 55); y += 60; } catch { /* ignore */ }
    }
    doc.setFontSize(7.5); doc.setTextColor(120);
    doc.text(`Hash de verificação (SHA-256): ${hash || '—'}`, 14, y); y += 5;
    doc.text(`Documento gerado em ${new Date().toLocaleString('pt-BR')} pelo sistema Coala.`, 14, y);
    doc.save(`${titulo.replace(/[^a-zA-Z0-9]+/g, '_')}_${emp.nome.replace(/[^a-zA-Z0-9]+/g, '_')}.pdf`);
  } catch {
    alert('Não foi possível gerar o PDF neste dispositivo.');
  }
}

interface DocLike {
  titulo: string;
  texto?: string;
}
interface DocSigRecord {
  nomeDigitado: string;
  assinadoEm: string;
  hash?: string;
}

const MARGIN = 14;
const PAGE_W = 210;
const PAGE_H = 297;
const CONTENT_W = PAGE_W - MARGIN * 2;
const PAGE_BOTTOM = PAGE_H - 18;
const THUMB = 20;
const LINE_H = 5;

function footerNote(doc: any) {
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(150);
  doc.text(`Gerado em ${new Date().toLocaleString('pt-BR')} pelo sistema Coala.`, MARGIN, PAGE_H - 8);
  doc.setTextColor(0);
}
function ensureSpace(doc: any, y: number, needed: number): number {
  if (y + needed > PAGE_BOTTOM) { doc.addPage(); return 18; }
  return y;
}
function drawSignatureBlock(doc: any, y: number, signatureImg: string | null): number {
  y = ensureSpace(doc, y, 30);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.text('Assinatura:', MARGIN, y); y += 4;
  if (signatureImg) { try { doc.addImage(signatureImg, 'PNG', MARGIN, y, 50, 22); y += 24; } catch { y += 4; } }
  return y + 8;
}

interface DayPunch {
  tipo: string;
  time: string;
  lat?: number;
  lng?: number;
  foraDoEsperado?: boolean;
  justificativa?: string;
  fotoImg?: string | null;
}
export interface DayGroup {
  employeeId: string;
  emp: EmpLike;
  date: string;
  punches: DayPunch[];
  signatureImg: string | null;
}

const PUNCH_ORDER = ['entrada', 'saida_descanso', 'volta_descanso', 'saida'];
const PUNCH_LABEL: Record<string, string> = { entrada: 'Entrada', saida_descanso: 'Saída descanso', volta_descanso: 'Volta descanso', saida: 'Saída' };

function drawPontoDayFrame(doc: any, y: number, g: DayGroup): number {
  const completo = PUNCH_ORDER.every((tipo) => g.punches.some((p) => p.tipo === tipo));
  let contentH = 9;
  PUNCH_ORDER.forEach((tipo) => {
    const p = g.punches.find((x) => x.tipo === tipo);
    contentH += (p ? Math.max(LINE_H * 2, THUMB) : LINE_H) + 3;
  });
  contentH += 4;
  y = ensureSpace(doc, y, contentH);
  const frameTop = y;
  let cy = y + 7;

  doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
  doc.text(`Ponto — ${g.date.split('-').reverse().join('/')}`, MARGIN + 4, cy);
  doc.setFontSize(9);
  doc.setTextColor(completo ? 15 : 200, completo ? 130 : 60, completo ? 110 : 40);
  doc.text(completo ? 'COMPLETO' : 'INCOMPLETO', MARGIN + CONTENT_W - 32, cy);
  doc.setTextColor(0);
  cy += 7;

  PUNCH_ORDER.forEach((tipo) => {
    const p = g.punches.find((x) => x.tipo === tipo);
    if (p) {
      const blockH = Math.max(LINE_H * 2, THUMB);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
      doc.text(`${PUNCH_LABEL[tipo]}: ${new Date(p.time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`, MARGIN + 4, cy + LINE_H);
      doc.setFont('helvetica', 'normal');
      const flag = p.foraDoEsperado ? `  ⚠ fora do esperado${p.justificativa ? ` (${p.justificativa})` : ''}` : '';
      doc.text(`Localização: ${p.lat ?? '—'}, ${p.lng ?? '—'}${flag}`, MARGIN + 4, cy + LINE_H * 2);
      if (p.fotoImg) { try { doc.addImage(p.fotoImg, 'JPEG', MARGIN + CONTENT_W - THUMB - 4, cy, THUMB, THUMB); } catch { /* ignore */ } }
      cy += blockH + 3;
    } else {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(200, 60, 40);
      doc.text(`${PUNCH_LABEL[tipo]}: não registrado`, MARGIN + 4, cy + LINE_H);
      doc.setTextColor(0);
      cy += LINE_H + 3;
    }
  });

  doc.setDrawColor(180);
  doc.rect(MARGIN, frameTop, CONTENT_W, contentH);
  return frameTop + contentH + 6;
}

function drawEmployeeHeader(doc: any, y: number, emp: EmpLike): number {
  y = ensureSpace(doc, y, 16);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13);
  doc.text(emp.nome, MARGIN, y); y += 6;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(90);
  doc.text(`CPF: ${emp.cpf || '—'}    Função: ${emp.funcao || '—'}    Camarote: ${emp.camarote || '—'}`, MARGIN, y);
  doc.setTextColor(0);
  return y + 8;
}

function renderPontoEmployee(doc: any, y: number, groups: DayGroup[]): number {
  y = drawEmployeeHeader(doc, y, groups[0].emp);
  groups.forEach((g) => { y = drawPontoDayFrame(doc, y, g); });
  y = drawSignatureBlock(doc, y, groups[0].signatureImg);
  return y;
}

/** Espera os grupos já ordenados por funcionário (mesmo employeeId adjacente). */
export async function generateDayReceiptPDF(groups: DayGroup[], filename = 'folha_de_ponto.pdf') {
  try {
    await ensureJsPDF();
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF();
    let y = 18;
    let firstEmployee = true;
    for (const employeeId of [...new Set(groups.map((g) => g.employeeId))]) {
      const employeeGroups = groups.filter((g) => g.employeeId === employeeId);
      if (!firstEmployee) { doc.addPage(); y = 18; }
      y = renderPontoEmployee(doc, y, employeeGroups);
      firstEmployee = false;
    }
    footerNote(doc);
    doc.save(filename);
  } catch {
    alert('Não foi possível gerar o PDF neste dispositivo.');
  }
}

/** Um PDF por funcionário, compactados num .zip. */
export async function generateDayReceiptPDFsZip(groups: DayGroup[], zipFilename: string) {
  await ensureJsPDF();
  const { jsPDF } = window.jspdf;
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  for (const employeeId of [...new Set(groups.map((g) => g.employeeId))]) {
    const employeeGroups = groups.filter((g) => g.employeeId === employeeId);
    const doc = new jsPDF();
    renderPontoEmployee(doc, 18, employeeGroups);
    footerNote(doc);
    const safeName = employeeGroups[0].emp.nome.replace(/[^a-zA-Z0-9]+/g, '_');
    zip.file(`${safeName}.pdf`, doc.output('arraybuffer'));
  }
  const blob = await zip.generateAsync({ type: 'blob' });
  downloadBlob(blob, zipFilename);
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

interface AlimEntry { date: string; hora: string; lat?: number; lng?: number; fotoImg?: string | null; }
export interface AlimGroup { employeeId: string; emp: EmpLike; entries: AlimEntry[]; signatureImg: string | null; }

function drawAlimFrame(doc: any, y: number, e: AlimEntry): number {
  const contentH = Math.max(LINE_H * 2, THUMB) + 10;
  y = ensureSpace(doc, y, contentH);
  const frameTop = y;
  let cy = y + 7;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
  doc.text(`Vale-alimentação — ${e.date.split('-').reverse().join('/')}`, MARGIN + 4, cy);
  cy += 6;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  doc.text(`Hora: ${new Date(e.hora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`, MARGIN + 4, cy);
  doc.text(`Localização: ${e.lat ?? '—'}, ${e.lng ?? '—'}`, MARGIN + 4, cy + LINE_H);
  if (e.fotoImg) { try { doc.addImage(e.fotoImg, 'JPEG', MARGIN + CONTENT_W - THUMB - 4, y + 6, THUMB, THUMB); } catch { /* ignore */ } }
  doc.setDrawColor(180);
  doc.rect(MARGIN, frameTop, CONTENT_W, contentH);
  return frameTop + contentH + 6;
}

function renderAlimEmployee(doc: any, y: number, group: AlimGroup): number {
  y = drawEmployeeHeader(doc, y, group.emp);
  group.entries.forEach((e) => { y = drawAlimFrame(doc, y, e); });
  y = drawSignatureBlock(doc, y, group.signatureImg);
  return y;
}

export async function generateAlimReceiptsPDF(groups: AlimGroup[], filename: string) {
  try {
    await ensureJsPDF();
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF();
    let y = 18;
    groups.forEach((g, idx) => { if (idx > 0) { doc.addPage(); y = 18; } y = renderAlimEmployee(doc, y, g); });
    footerNote(doc);
    doc.save(filename);
  } catch {
    alert('Não foi possível gerar o PDF neste dispositivo.');
  }
}

export async function generateAlimReceiptsPDFsZip(groups: AlimGroup[], zipFilename: string) {
  await ensureJsPDF();
  const { jsPDF } = window.jspdf;
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  for (const g of groups) {
    const doc = new jsPDF();
    renderAlimEmployee(doc, 18, g);
    footerNote(doc);
    zip.file(`${g.emp.nome.replace(/[^a-zA-Z0-9]+/g, '_')}.pdf`, doc.output('arraybuffer'));
  }
  const blob = await zip.generateAsync({ type: 'blob' });
  downloadBlob(blob, zipFilename);
}

interface RecvEntry { tipoLabel: string; descricao: string; dataHora: string; lat?: number; lng?: number; fotoImg?: string | null; }
export interface RecvGroup { employeeId: string; emp: EmpLike; entries: RecvEntry[]; signatureImg: string | null; }

function drawRecvFrame(doc: any, y: number, e: RecvEntry): number {
  const contentH = Math.max(LINE_H * 3, THUMB) + 10;
  y = ensureSpace(doc, y, contentH);
  const frameTop = y;
  let cy = y + 7;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
  doc.text(`Recebimento — ${e.tipoLabel}`, MARGIN + 4, cy);
  cy += 6;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  doc.text(`Descrição: ${e.descricao}`, MARGIN + 4, cy);
  doc.text(`Data/Hora: ${new Date(e.dataHora).toLocaleString('pt-BR')}`, MARGIN + 4, cy + LINE_H);
  doc.text(`Localização: ${e.lat ?? '—'}, ${e.lng ?? '—'}`, MARGIN + 4, cy + LINE_H * 2);
  if (e.fotoImg) { try { doc.addImage(e.fotoImg, 'JPEG', MARGIN + CONTENT_W - THUMB - 4, y + 6, THUMB, THUMB); } catch { /* ignore */ } }
  doc.setDrawColor(180);
  doc.rect(MARGIN, frameTop, CONTENT_W, contentH);
  return frameTop + contentH + 6;
}

function renderRecvEmployee(doc: any, y: number, group: RecvGroup): number {
  y = drawEmployeeHeader(doc, y, group.emp);
  group.entries.forEach((e) => { y = drawRecvFrame(doc, y, e); });
  y = drawSignatureBlock(doc, y, group.signatureImg);
  return y;
}

export async function generateRecebimentosReceiptsPDF(groups: RecvGroup[], filename: string) {
  try {
    await ensureJsPDF();
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF();
    let y = 18;
    groups.forEach((g, idx) => { if (idx > 0) { doc.addPage(); y = 18; } y = renderRecvEmployee(doc, y, g); });
    footerNote(doc);
    doc.save(filename);
  } catch {
    alert('Não foi possível gerar o PDF neste dispositivo.');
  }
}

export async function generateRecebimentosReceiptsPDFsZip(groups: RecvGroup[], zipFilename: string) {
  await ensureJsPDF();
  const { jsPDF } = window.jspdf;
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  for (const g of groups) {
    const doc = new jsPDF();
    renderRecvEmployee(doc, 18, g);
    footerNote(doc);
    zip.file(`${g.emp.nome.replace(/[^a-zA-Z0-9]+/g, '_')}.pdf`, doc.output('arraybuffer'));
  }
  const blob = await zip.generateAsync({ type: 'blob' });
  downloadBlob(blob, zipFilename);
}

export async function generateDocPDF(doc: DocLike, emp: EmpLike, docSigRecord: DocSigRecord, signatureImg: string | null, applyTemplate: (t: string, e: any) => string) {
  try {
    await ensureJsPDF();
    const { jsPDF } = window.jspdf;
    const pdf = new jsPDF();
    let y = 18;
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14);
    pdf.text(doc.titulo, 14, y); y += 8;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
    const texto = applyTemplate(doc.texto || '', emp);
    const lines: string[] = pdf.splitTextToSize(texto, 180);
    lines.forEach((line: string) => {
      if (y > 270) { pdf.addPage(); y = 18; }
      pdf.text(line, 14, y); y += 5;
    });
    y += 8;
    if (y > 230) { pdf.addPage(); y = 18; }
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10); pdf.text('Assinatura:', 14, y); y += 4;
    if (signatureImg) { try { pdf.addImage(signatureImg, 'PNG', 14, y, 60, 26); y += 30; } catch { y += 6; } }
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
    pdf.text(`${docSigRecord.nomeDigitado} — assinado em ${new Date(docSigRecord.assinadoEm).toLocaleString('pt-BR')}`, 14, y); y += 6;
    pdf.setFontSize(7.5); pdf.setTextColor(120);
    pdf.text(`Hash de verificação: ${docSigRecord.hash || '—'}`, 14, y);
    pdf.save(`${doc.titulo.replace(/[^a-zA-Z0-9]+/g, '_')}_${emp.nome.replace(/[^a-zA-Z0-9]+/g, '_')}.pdf`);
  } catch {
    alert('Não foi possível gerar o PDF.');
  }
}
